import React from 'react';
import { useLocation } from 'react-router-dom';
import { useSubsidiary, ALL_SUBSIDIARIES_SENTINEL } from '@/contexts/SubsidiaryContext';
import { usePeriod } from '@/contexts/PeriodContext';
import { useRoleView } from '@/contexts/RoleViewContext';
import { useAuthStatus } from '@/hooks/useApi';
import { Select } from '@/components/ui/Select';
import { ViewSelector } from '@/components/ui/ViewSelector';
import { 
  Building2, 
  Calendar, 
  Clock,
  AlertCircle 
} from 'lucide-react';

// Pages that should show the context bar
const PAGES_WITH_CONTEXT = [
  '/dashboard',
  '/reconciliation',
  '/accounts',
  '/admin/accounts',
  '/admin/periods',
  '/admin/assignments',
  '/delegations',
  '/bankrec'
];

// Pages that need period selector
const PAGES_WITH_PERIOD = [
  '/dashboard',
  '/reconciliation',
  '/accounts',
  '/admin/periods',
  '/bankrec'
];

// Pages that need role selector  
const PAGES_WITH_ROLE = [
  '/dashboard',
  '/reconciliation'
];

// Pages that need subsidiary selector  
const PAGES_WITH_SUBSIDIARY = [
  '/dashboard',
  '/reconciliation',
  '/accounts',
  '/admin/accounts',
  '/admin/periods',
  '/admin/assignments',
  '/delegations'
];


const ContextBar: React.FC = () => {
  const location = useLocation();
  const { data: authStatus } = useAuthStatus();
  const { selectedSubsidiary, subsidiaries, setSelectedSubsidiary, isLoading: subsidiariesLoading } = useSubsidiary();
  const { selectedPeriod, periods, currentPeriod, setSelectedPeriod, isLoading: periodsLoading } = usePeriod();
  const { availableViews } = useRoleView();

  // Check if current page should show context bar
  const shouldShowContextBar = PAGES_WITH_CONTEXT.some(path => 
    location.pathname === path || location.pathname.startsWith(path + '/')
  );

  const shouldShowPeriod = PAGES_WITH_PERIOD.some(path =>
    location.pathname === path || location.pathname.startsWith(path + '/')
  );

  const shouldShowRole = PAGES_WITH_ROLE.some(path =>
    location.pathname === path || location.pathname.startsWith(path + '/')
  );

  // Check if we're on a reconciliation detail page
  const isReconciliationDetail = location.pathname.match(/^\/reconciliation\/\d+$/);
  
  // Determine which dropdowns to show
  const shouldShowSubsidiary = !isReconciliationDetail && PAGES_WITH_SUBSIDIARY.some(path =>
    location.pathname === path || location.pathname.startsWith(path + '/')
  );

  if (!shouldShowContextBar || !authStatus?.user) {
    return null;
  }

  const getPeriodStatusIcon = (period: any) => {
    switch (period.status) {
      case 'OPEN':
        return <Clock className="h-3 w-3 text-green-500" />;
      case 'LOCKED':
        return <AlertCircle className="h-3 w-3 text-yellow-500" />;
      case 'CLOSED':
        return <AlertCircle className="h-3 w-3 text-red-500" />;
      default:
        return null;
    }
  };

  const getPeriodStatusBadge = (period: any) => {
    const baseClasses = "ml-1 px-1.5 py-0.5 text-xs font-medium rounded";
    switch (period.status) {
      case 'OPEN':
        return `${baseClasses} bg-green-100 text-green-700`;
      case 'LOCKED':
        return `${baseClasses} bg-yellow-100 text-yellow-700`;
      case 'CLOSED':
        return `${baseClasses} bg-red-100 text-red-700`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-700`;
    }
  };

  return (
    <div className="bg-white border-b border-gray-200 px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left side - empty for now, could add page title or breadcrumbs */}
        <div></div>

        {/* Right side - Context selectors */}
        <div className="flex items-center space-x-8">
          {/* Subsidiary Selector */}
          {shouldShowSubsidiary && (
            <div className="flex items-center space-x-4">
              <Building2 className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 min-w-max">Subsidiary:</span>
              <Select
                variant="small"
                value={selectedSubsidiary && selectedSubsidiary.id !== 0 ? selectedSubsidiary.id : ''}
                onChange={(e) => {
                  if (!e.target.value) {
                    setSelectedSubsidiary(ALL_SUBSIDIARIES_SENTINEL);
                    return;
                  }
                  const subsidiaryId = parseInt(e.target.value);
                  const subsidiary = subsidiaries.find(s => s.id === subsidiaryId);
                  if (subsidiary) {
                    setSelectedSubsidiary(subsidiary);
                  }
                }}
                disabled={subsidiariesLoading}
                className="min-w-56"
              >
                <option value="">All Subsidiaries</option>
                {subsidiaries.map(subsidiary => (
                  <option key={subsidiary.id} value={subsidiary.id}>
                    {subsidiary.name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {/* Period Selector */}
          {shouldShowPeriod && (
            <div className="flex items-center space-x-4">
              <Calendar className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700 min-w-max" title="Filter reconciliations by period">Period:</span>
              <div className="flex items-center space-x-2">
                <Select
                  variant="small"
                  value={selectedPeriod?.id || ''}
                  onChange={(e) => {
                    const periodId = e.target.value ? parseInt(e.target.value) : null;
                    const period = periods.find(p => p.id === periodId) || null;
                    setSelectedPeriod(period);
                    
                    // If we're on a reconciliation detail page and period changes,
                    // navigate to the reconciliation list for the new period
                    if (isReconciliationDetail && period) {
                      window.location.href = '/reconciliation';
                    }
                  }}
                  disabled={periodsLoading}
                  className="min-w-64"
                >
                  <option value="">Select Period</option>
                  {periods.map(period => (
                    <option key={period.id} value={period.id}>
                      {period.accounting_period.period_name}
                    </option>
                  ))}
                </Select>
                {selectedPeriod && (
                  <div className="flex items-center">
                    {getPeriodStatusIcon(selectedPeriod)}
                    <span className={getPeriodStatusBadge(selectedPeriod)}>
                      {selectedPeriod.status.toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Role View Selector */}
          {shouldShowRole && availableViews.length > 1 && (
            <ViewSelector />
          )}
        </div>
      </div>
    </div>
  );
};

export default ContextBar;